import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { INDUSTRY_PAGES, INDUSTRY_LIST } from '@/data/industries';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, AlertTriangle, ScanLine, CheckCircle2, Camera, Layers,
  Activity, ShieldCheck, LineChart, MapPin,
} from 'lucide-react';

const signalIcons = [Camera, ScanLine, MapPin, ShieldCheck];
const intelIcons = [Activity, ShieldCheck, LineChart];

function ProblemBlock({ problem }) {
  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">The problem</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">
            {problem.heading}
          </h2>
        </div>
        <div className="md:col-span-8">
          <ul className="space-y-4">
            {problem.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-200">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-rose-400/20 bg-rose-500/[0.06] px-5 py-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mb-1">Result</div>
            <p className="text-sm text-slate-200 leading-relaxed">{problem.result}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SolveBlock({ solve, solveTag }) {
  return (
    <section className="border-t border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">How we solve this</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 max-w-3xl leading-tight">
          Every container checked. Every checkpoint recorded.
        </h2>
        <p className="text-slate-400 mt-4 max-w-3xl">{solve}</p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-300 px-3 py-1.5 rounded-md bg-cyan-400/10 border border-cyan-400/30">
          <span className="dot-pulse" /> {solveTag}
        </div>
      </div>
    </section>
  );
}

function SignalsBlock({ signals, accent }) {
  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">What the system sees</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 max-w-3xl leading-tight">
          Four signals, captured on every single container.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {signals.map((s, i) => {
            const Icon = signalIcons[i] || Camera;
            return (
              <div key={i} className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6 hover:border-cyan-400/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-cyan-300" />
                  </div>
                  <div className="font-display text-lg font-semibold">{s.title}</div>
                </div>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FigBlock({ num, kicker, figSpecs, figCaption }) {
  return (
    <section className="border-t border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 viso-grid relative overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-3">
              <ScanLine className="w-4 h-4 text-cyan-300" />
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300">
                Fig. {num} — {kicker}
              </div>
            </div>
            <div className="text-[10px] font-mono text-cyan-300 flex items-center gap-1.5">
              <span className="dot-pulse" /> live capture
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {figSpecs.map((f, i) => (
              <div key={i} className="rounded-lg border border-white/5 bg-black/40 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{f.l}</div>
                <div className="font-display text-lg font-semibold text-cyan-300 mt-1">{f.v}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-400 mt-6 max-w-2xl">{figCaption}</p>
          <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        </div>
      </div>
    </section>
  );
}

function WorksForBlock({ worksFor }) {
  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">Works for everything you move</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 max-w-3xl leading-tight">
          {worksFor.heading}
        </h2>
        <div className="mt-8 flex flex-wrap gap-2">
          {worksFor.items.map((t) => (
            <span key={t} className="px-4 py-2 rounded-full text-sm bg-white/[0.03] border border-white/10 text-slate-200 hover:border-cyan-400/30 hover:bg-white/[0.06] transition-colors">
              {t}
            </span>
          ))}
        </div>
        <p className="text-sm text-slate-400 mt-6 max-w-2xl">{worksFor.footer}</p>
      </div>
    </section>
  );
}

function IntelBlock({ intel }) {
  return (
    <section className="border-t border-white/5 bg-[#0a0d12]">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300">Operational intelligence</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 max-w-3xl leading-tight">
          Real data — captured automatically, every shift.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {intel.map((col, i) => {
            const Icon = intelIcons[i] || Activity;
            return (
              <div key={col.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-cyan-300" />
                  </div>
                  <div className="font-display font-semibold">{col.title}</div>
                </div>
                <ul className="mt-4 space-y-2">
                  {col.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-cyan-300 mt-1 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function IndustryPage() {
  const { slug } = useParams();
  const data = INDUSTRY_PAGES[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <Navigate to="/industries" replace />;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={data.poster} alt={data.kicker} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07090c]/70 via-[#07090c]/85 to-[#07090c]" />
          <div className="absolute inset-0 viso-grid opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-4">
            {data.num} · {data.kicker}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.02] max-w-4xl">
            <span className={`bg-gradient-to-r ${data.accent} bg-clip-text text-transparent`}>
              {data.title}
            </span>
          </h1>
          <p className="text-slate-200 mt-4 text-lg md:text-xl max-w-3xl">{data.subtitle}</p>
          <p className="text-slate-400 mt-6 max-w-3xl leading-relaxed">{data.hero}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {data.pills.map((p) => (
              <span key={p} className="px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider bg-white/5 border border-white/10 text-cyan-200">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact">
              <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                Book a live demo <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/industries">
              <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">
                All industries
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <ProblemBlock problem={data.problem} />
      <SolveBlock solve={data.solve} solveTag={data.solveTag} />
      <SignalsBlock signals={data.signals} accent={data.accent} />
      <FigBlock num={data.num} kicker={data.kicker} figSpecs={data.figSpecs} figCaption={data.figCaption} />
      <WorksForBlock worksFor={data.worksFor} />
      <IntelBlock intel={data.intel} />

      {/* Footer / CTA */}
      <section className="border-t border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 md:p-14">
            <div className="absolute inset-0 viso-grid opacity-60" />
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-400/15 blur-3xl" />
            <div className="relative">
              <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">{data.footer}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                    Book a live demo <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/platform">
                  <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">
                    Explore the platform
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore other industries */}
      <section className="border-t border-white/5 pb-20">
        <div className="max-w-7xl mx-auto px-6 pt-12">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-5">Other industries</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {INDUSTRY_LIST.filter((i) => i.slug !== slug).map((i) => (
              <Link
                key={i.slug}
                to={`/industries/${i.slug}`}
                className="group rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-cyan-400/30 hover:bg-white/[0.04] transition-all"
              >
                <div className="text-[10px] font-mono text-slate-500">{i.num}</div>
                <div className="font-display font-semibold text-slate-100 mt-1 group-hover:text-cyan-300 transition-colors">
                  {i.kicker}
                </div>
                <div className="inline-flex items-center gap-1 text-xs text-cyan-300 mt-3">
                  Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
